import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, HelpCircle, FileText, Camera, Download, Smartphone, AlertCircle, Mail, CreditCard, Home, CheckCircle2 } from 'lucide-react';
import Layout from '../components/layout/Layout';

export default function Help() {
  const navigate = useNavigate();
  const [openAccordion, setOpenAccordion] = useState(null);

  const toggleAccordion = (id) => {
    setOpenAccordion(openAccordion === id ? null : id);
  };

  const quickTopics = [
    {
      icon: CreditCard,
      title: 'Plany i subskrypcja',
      description: 'Limity, okres próbny, płatności',
      href: '#plans'
    },
    {
      icon: Home,
      title: 'Inspekcje',
      description: 'Typy, tworzenie, porównywanie',
      href: '#inspections'
    },
    {
      icon: Camera,
      title: 'Zdjęcia',
      description: 'Limity zdjęć, upload, jakość',
      href: '#photos'
    },
    {
      icon: Download,
      title: 'Eksport PDF',
      description: 'Generowanie protokołów',
      href: '#pdf'
    },
    {
      icon: Smartphone,
      title: 'Aplikacja PWA',
      description: 'Instalacja na telefonie',
      href: '#pwa'
    },
    {
      icon: AlertCircle,
      title: 'Rozwiązywanie problemów',
      description: 'Najczęstsze błędy i ich naprawy',
      href: '#troubleshooting'
    }
  ];

  const troubleshootingFAQ = [
    {
      id: 'faq-1',
      question: 'Nie mogę dodać kolejnej nieruchomości',
      answer: 'Plan darmowy pozwala na 1 nieruchomość, plan Starter na 1 (z pełnymi funkcjami Pro), plan Pro na 5. Sprawdź swój plan w Ustawieniach → Subskrypcja. Możesz rozpocząć 14-dniowy bezpłatny okres próbny (Starter lub Pro).'
    },
    {
      id: 'faq-2',
      question: 'Inspekcja okresowa jest niedostępna',
      answer: 'Inspekcje okresowe są dostępne w planach Starter i Pro. Plan darmowy umożliwia tworzenie jedynie inspekcji wprowadzenia i wyprowadzenia. Rozpocznij 14-dniowy okres próbny (Starter lub Pro), aby uzyskać dostęp do wszystkich typów inspekcji.'
    },
    {
      id: 'faq-3',
      question: 'Nie mogę dodać więcej zdjęć',
      answer: 'Plan darmowy: maksymalnie 5 zdjęć na całą inspekcję. Plan Starter i Pro: maksymalnie 4 zdjęcia na element (unlimited elementów). Licznik zdjęć jest oznaczony kolorami: szary (OK), pomarańczowy (blisko limitu), czerwony (osiągnięto limit).'
    },
    {
      id: 'faq-4',
      question: 'Eksport PDF nie działa / przycisk zablokowany',
      answer: 'PDF jest dostępny dla wszystkich użytkowników (Free i Pro). Jeśli przycisk jest zablokowany, spróbuj: 1) Odśwież stronę 2) Sprawdź, czy inspekcja została zapisana 3) Wyloguj się i zaloguj ponownie'
    },
    {
      id: 'faq-5',
      question: 'Zdjęcia nie ładują się po zapisaniu',
      answer: 'Jeśli zdjęcia nie wyświetlają się po zapisaniu inspekcji: 1) Sprawdź połączenie internetowe 2) Upewnij się, że zdjęcia miały mniej niż 10 MB 3) Spróbuj ponownie dodać zdjęcia mniejszej rozdzielczości'
    },
    {
      id: 'faq-6',
      question: 'Aplikacja działa wolno / zawiesza się',
      answer: 'Spróbuj: 1) Wyczyść pamięć podręczną przeglądarki 2) Zamknij inne karty 3) Zrestartuj przeglądarkę 4) Użyj Chrome lub Safari (zalecane) 5) Sprawdź połączenie internetowe'
    },
    {
      id: 'faq-7',
      question: 'Nie widzę zapisanych inspekcji',
      answer: 'Sprawdź: 1) Czy jesteś zalogowany na właściwe konto 2) Czy inspekcja została zapisana (kliknij "Zapisz inspekcję") 3) Czy wybrałeś właściwą nieruchomość na liście'
    },
    {
      id: 'faq-8',
      question: 'Porównanie inspekcji nie pokazuje różnic',
      answer: 'Upewnij się, że: 1) Porównujesz inspekcje tej samej nieruchomości 2) Oba protokoły zostały zapisane 3) Włącz opcję "Tylko różnice" w widoku porównania'
    }
  ];

  return (
    <Layout>
      <div className="max-w-4xl mx-auto p-6">
        {/* Back Button */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors group"
        >
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          <span className="font-medium">Powrót do panelu</span>
        </button>

        {/* White Card Container */}
        <div className="bg-white border border-slate-200 rounded-xl p-8 shadow-sm">
          <h1 className="text-3xl font-bold mb-2">Centrum Pomocy</h1>
          <p className="text-slate-600 mb-8">
            Znajdź odpowiedzi na najczęściej zadawane pytania
          </p>

          {/* Contact Section */}
          <div className="mb-8 p-6 bg-violet-50 border border-violet-200 rounded-xl">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-violet-600 rounded-xl flex items-center justify-center flex-shrink-0">
                <Mail className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-slate-800 mb-1">
                  Potrzebujesz pomocy?
                </h3>
                <p className="text-sm text-slate-600 mb-3">
                  Nasz zespół wsparcia jest tutaj, aby Ci pomóc. Odpowiadamy zazwyczaj w ciągu 24 godzin.
                </p>
                <a
                  href="mailto:hello@propertycheck.pro"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg font-medium hover:bg-violet-700 transition-colors text-sm"
                >
                  <Mail className="w-4 h-4" />
                  Skontaktuj się z nami
                </a>
              </div>
            </div>
          </div>

        {/* Quick Topics Grid */}
        <div className="mb-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {quickTopics.map((topic, index) => {
              const Icon = topic.icon;
              const isTroubleshooting = topic.href === '#troubleshooting';
              return (
                <a
                  key={index}
                  href={topic.href}
                  className="bg-slate-50 border border-slate-200 rounded-xl p-6 hover:border-violet-300 hover:bg-white hover:shadow-sm transition-all duration-200 group"
                >
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 transition-colors ${
                    isTroubleshooting
                      ? 'bg-red-500 group-hover:bg-red-600'
                      : 'bg-slate-100 group-hover:bg-violet-50'
                  }`}>
                    <Icon className={`w-6 h-6 transition-colors ${
                      isTroubleshooting
                        ? 'text-white'
                        : 'text-slate-700 group-hover:text-violet-600'
                    }`} />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">
                    {topic.title}
                  </h3>
                  <p className="text-sm text-slate-600">
                    {topic.description}
                  </p>
                </a>
              );
            })}
          </div>
        </div>

        {/* Detailed Sections */}
        <div className="space-y-12 pb-8">

          {/* Plans Section */}
          <section id="plans" className="scroll-mt-20">
            <div className="border-t border-slate-200 pt-8 pb-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-slate-700" />
                </div>
                <h2 className="text-3xl font-bold text-slate-800">Plany i subskrypcja</h2>
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold text-slate-800 mb-3">Plan Free (Darmowy)</h3>
                  <ul className="space-y-2 text-slate-700">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-slate-600 mt-0.5 flex-shrink-0" />
                      <span><strong>1 nieruchomość</strong> - idealne na początek</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-slate-600 mt-0.5 flex-shrink-0" />
                      <span><strong>2 inspekcje na nieruchomość</strong> - wprowadzenie + wyprowadzenie</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-slate-600 mt-0.5 flex-shrink-0" />
                      <span><strong>5 zdjęć na całą inspekcję</strong></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-slate-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Eksport PDF</strong> - pełne protokoły</span>
                    </li>
                  </ul>
                </div>

                <div className="border-t pt-6">
                  <h3 className="text-xl font-semibold text-slate-800 mb-3">Plan Starter</h3>
                  <ul className="space-y-2 text-slate-700 mb-4">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-slate-600 mt-0.5 flex-shrink-0" />
                      <span><strong>1 nieruchomość</strong> - z pełnymi funkcjami Pro</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-slate-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Nieograniczone inspekcje</strong> - wszystkie typy</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-slate-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Inspekcje okresowe</strong> - pełna kontrola najmu</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-slate-600 mt-0.5 flex-shrink-0" />
                      <span><strong>4 zdjęcia na element</strong> - szczegółowa dokumentacja</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-slate-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Porównywanie inspekcji</strong></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-slate-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Eksport PDF</strong></span>
                    </li>
                  </ul>
                  <div className="bg-violet-50 border border-violet-200 rounded-xl p-4">
                    <p className="text-violet-900 font-semibold mb-1">💎 14 dni za darmo!</p>
                    <p className="text-sm text-violet-800">
                      Idealne dla właścicieli jednej nieruchomości. Po okresie próbnym tylko <strong>29 PLN/miesiąc</strong> lub <strong>290 PLN/rok</strong>.
                    </p>
                  </div>
                </div>

                <div className="border-t pt-6">
                  <h3 className="text-xl font-semibold text-slate-800 mb-3">Plan Pro</h3>
                  <ul className="space-y-2 text-slate-700 mb-4">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-slate-600 mt-0.5 flex-shrink-0" />
                      <span><strong>5 nieruchomości</strong></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-slate-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Nieograniczone inspekcje</strong> - wszystkie typy</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-slate-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Inspekcje okresowe</strong> - pełna kontrola najmu</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-slate-600 mt-0.5 flex-shrink-0" />
                      <span><strong>4 zdjęcia na element</strong> - szczegółowa dokumentacja</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-slate-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Eksport PDF</strong></span>
                    </li>
                  </ul>
                  <div className="bg-violet-50 border border-violet-200 rounded-xl p-4">
                    <p className="text-violet-900 font-semibold mb-1">💎 14 dni za darmo!</p>
                    <p className="text-sm text-violet-800">
                      Idealne dla portfolio nieruchomości. Po okresie próbnym tylko <strong>49 PLN/miesiąc</strong> lub <strong>490 PLN/rok</strong>.
                    </p>
                  </div>
                </div>

                <div className="border-t pt-6">
                  <h3 className="text-xl font-semibold text-slate-800 mb-3">Jak zmienić plan?</h3>
                  <ol className="list-decimal list-inside space-y-2 text-slate-700">
                    <li>Przejdź do <strong>Ustawienia → Subskrypcja</strong></li>
                    <li>Kliknij <strong>"Rozpocznij okres próbny"</strong> lub <strong>"Przejdź do płatności"</strong></li>
                    <li>Po aktywacji możesz zarządzać subskrypcją przez przycisk <strong>"Zarządzaj subskrypcją"</strong></li>
                  </ol>
                </div>
              </div>
            </div>
          </section>

          {/* Inspections Section */}
          <section id="inspections" className="scroll-mt-20">
            <div className="border-t border-slate-200 pt-8 pb-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center">
                  <Home className="w-6 h-6 text-slate-700" />
                </div>
                <h2 className="text-3xl font-bold text-slate-800">Inspekcje</h2>
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold text-slate-800 mb-3">Typy inspekcji</h3>
                  <div className="space-y-3">
                    <div className="border-l-4 border-green-500 pl-4">
                      <h4 className="font-semibold text-slate-800">Wprowadzenie</h4>
                      <p className="text-sm text-slate-600">
                        Dokumentuje stan nieruchomości przed wprowadzeniem lokatora. Zalecane przed podpisaniem umowy najmu.
                      </p>
                    </div>
                    <div className="border-l-4 border-blue-500 pl-4">
                      <h4 className="font-semibold text-slate-800">Okresowa <span className="text-xs bg-violet-100 text-violet-700 px-2 py-1 rounded-full">STARTER</span> <span className="text-xs bg-violet-100 text-violet-700 px-2 py-1 rounded-full">PRO</span></h4>
                      <p className="text-sm text-slate-600">
                        Kontrola stanu w trakcie najmu (np. co 6-12 miesięcy). Pomaga wykryć problemy wcześnie.
                      </p>
                    </div>
                    <div className="border-l-4 border-orange-500 pl-4">
                      <h4 className="font-semibold text-slate-800">Wyprowadzenie</h4>
                      <p className="text-sm text-slate-600">
                        Dokumentuje stan po wyprowadzce lokatora. Służy do porównania z inspekcją wprowadzenia.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-6">
                  <h3 className="text-xl font-semibold text-slate-800 mb-3">Jak utworzyć inspekcję?</h3>
                  <ol className="list-decimal list-inside space-y-2 text-slate-700">
                    <li>Wybierz nieruchomość z listy</li>
                    <li>Kliknij <strong>"Nowa inspekcja"</strong></li>
                    <li>Wypełnij podstawowe dane (typ, data, inspektor)</li>
                    <li>Przejdź przez pomieszczenia i oceń stan elementów</li>
                    <li>Dodaj zdjęcia i uwagi (opcjonalnie)</li>
                    <li>Kliknij <strong>"Zapisz inspekcję"</strong></li>
                  </ol>
                </div>

                <div className="border-t pt-6">
                  <h3 className="text-xl font-semibold text-slate-800 mb-3">Porównywanie inspekcji</h3>
                  <p className="text-slate-700 mb-3">
                    Porównanie pozwala zobaczyć, co zmieniło się między dwoma inspekcjami (np. wprowadzenie vs wyprowadzenie).
                  </p>
                  <ol className="list-decimal list-inside space-y-2 text-slate-700">
                    <li>Otwórz szczegóły nieruchomości</li>
                    <li>Kliknij <strong>"Porównaj inspekcje"</strong></li>
                    <li>Wybierz dwie inspekcje do porównania</li>
                    <li>Włącz <strong>"Tylko różnice"</strong>, aby zobaczyć zmienione elementy</li>
                  </ol>
                </div>
              </div>
            </div>
          </section>

          {/* Photos Section */}
          <section id="photos" className="scroll-mt-20">
            <div className="border-t border-slate-200 pt-8 pb-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center">
                  <Camera className="w-6 h-6 text-slate-700" />
                </div>
                <h2 className="text-3xl font-bold text-slate-800">Zdjęcia</h2>
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold text-slate-800 mb-3">Limity zdjęć</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="border border-slate-200 rounded-xl p-4">
                      <h4 className="font-semibold text-slate-800 mb-2">Plan Free</h4>
                      <p className="text-2xl font-bold text-slate-900 mb-1">5 zdjęć</p>
                      <p className="text-sm text-slate-600">na całą inspekcję</p>
                    </div>
                    <div className="border border-violet-300 bg-violet-50 rounded-xl p-4">
                      <h4 className="font-semibold text-violet-900 mb-2">Plan Starter i Pro</h4>
                      <p className="text-2xl font-bold text-violet-900 mb-1">4 zdjęcia</p>
                      <p className="text-sm text-violet-700">na każdy element</p>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-6">
                  <h3 className="text-xl font-semibold text-slate-800 mb-3">Wskaźnik limitu zdjęć</h3>
                  <p className="text-slate-700 mb-3">
                    Podczas dodawania zdjęć zobaczysz kolorowy licznik:
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <div className="w-20 h-8 bg-slate-200 rounded flex items-center justify-center text-sm font-semibold text-slate-700">
                        2 / 5
                      </div>
                      <span className="text-slate-700"><strong>Szary</strong> - masz jeszcze dużo miejsca</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-20 h-8 bg-orange-100 rounded flex items-center justify-center text-sm font-semibold text-orange-700">
                        4 / 5
                      </div>
                      <span className="text-slate-700"><strong>Pomarańczowy</strong> - zbliżasz się do limitu</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-20 h-8 bg-red-100 rounded flex items-center justify-center text-sm font-semibold text-red-700">
                        5 / 5
                      </div>
                      <span className="text-slate-700"><strong>Czerwony</strong> - osiągnięto limit</span>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-6">
                  <h3 className="text-xl font-semibold text-slate-800 mb-3">Wymagania techniczne</h3>
                  <ul className="space-y-2 text-slate-700">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-slate-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Formaty:</strong> JPG, PNG, WEBP</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-slate-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Maksymalny rozmiar:</strong> 10 MB na zdjęcie</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-slate-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Kompresja:</strong> Automatyczna optymalizacja w przeglądarce</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-slate-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Zalecenia:</strong> Zdjęcia poziome, dobre oświetlenie, wyraźne detale</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* PDF Section */}
          <section id="pdf" className="scroll-mt-20">
            <div className="border-t border-slate-200 pt-8 pb-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center">
                  <Download className="w-6 h-6 text-slate-700" />
                </div>
                <h2 className="text-3xl font-bold text-slate-800">Eksport PDF</h2>
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold text-slate-800 mb-3">Dostępność</h3>
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                    <p className="text-green-900 font-semibold mb-1">✅ Dostępne dla wszystkich użytkowników</p>
                    <p className="text-sm text-green-800">
                      Eksport PDF jest dostępny zarówno w planie Free, jak i Pro. Możesz pobierać pełne protokoły inspekcji.
                    </p>
                  </div>
                </div>

                <div className="border-t pt-6">
                  <h3 className="text-xl font-semibold text-slate-800 mb-3">Jak wyeksportować PDF?</h3>
                  <ol className="list-decimal list-inside space-y-2 text-slate-700">
                    <li>Otwórz szczegóły inspekcji</li>
                    <li>Kliknij przycisk <strong>"Pobierz PDF"</strong> u góry strony</li>
                    <li>Poczekaj 2-5 sekund na wygenerowanie dokumentu</li>
                    <li>Plik zostanie automatycznie pobrany na Twoje urządzenie</li>
                  </ol>
                </div>

                <div className="border-t pt-6">
                  <h3 className="text-xl font-semibold text-slate-800 mb-3">Co zawiera protokół PDF?</h3>
                  <ul className="space-y-2 text-slate-700">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-slate-600 mt-0.5 flex-shrink-0" />
                      <span>Dane nieruchomości i inspekcji</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-slate-600 mt-0.5 flex-shrink-0" />
                      <span>Lista wszystkich pomieszczeń</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-slate-600 mt-0.5 flex-shrink-0" />
                      <span>Tabela elementów ze stanem i uwagami</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-slate-600 mt-0.5 flex-shrink-0" />
                      <span>Liczba zdjęć dla każdego elementu</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-slate-600 mt-0.5 flex-shrink-0" />
                      <span>Uwagi ogólne (jeśli dodano)</span>
                    </li>
                  </ul>
                  <p className="text-sm text-slate-600 mt-3">
                    <strong>Uwaga:</strong> Zdjęcia nie są osadzone w PDF (tylko liczba zdjęć). To zmniejsza rozmiar pliku i przyspiesza generowanie.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* PWA Section */}
          <section id="pwa" className="scroll-mt-20">
            <div className="border-t border-slate-200 pt-8 pb-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center">
                  <Smartphone className="w-6 h-6 text-slate-700" />
                </div>
                <h2 className="text-3xl font-bold text-slate-800">Aplikacja PWA</h2>
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold text-slate-800 mb-3">Co to jest PWA?</h3>
                  <p className="text-slate-700 mb-3">
                    Progressive Web App (PWA) to technologia, która pozwala zainstalować PropertyCheck.PRO na Twoim telefonie jak normalną aplikację, bez potrzeby pobierania z App Store czy Google Play.
                  </p>
                  <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                    <p className="text-purple-900 font-semibold mb-1">📱 Zalety PWA:</p>
                    <ul className="text-sm text-purple-800 space-y-1">
                      <li>• Ikona na ekranie głównym</li>
                      <li>• Szybkie uruchamianie</li>
                      <li>• Działa w trybie pełnoekranowym</li>
                      <li>• Nie zajmuje dużo miejsca</li>
                    </ul>
                  </div>
                </div>

                <div className="border-t pt-6">
                  <h3 className="text-xl font-semibold text-slate-800 mb-3">Jak zainstalować na iPhone (Safari)?</h3>
                  <ol className="list-decimal list-inside space-y-2 text-slate-700">
                    <li>Otwórz <strong>propertycheck.pro</strong> w przeglądarce Safari</li>
                    <li>Kliknij ikonę <strong>"Udostępnij"</strong> (kwadrat ze strzałką w górę)</li>
                    <li>Przewiń w dół i wybierz <strong>"Dodaj do ekranu początkowego"</strong></li>
                    <li>Potwierdź nazwę i kliknij <strong>"Dodaj"</strong></li>
                    <li>Ikona PropertyCheck.PRO pojawi się na Twoim ekranie głównym</li>
                  </ol>
                </div>

                <div className="border-t pt-6">
                  <h3 className="text-xl font-semibold text-slate-800 mb-3">Jak zainstalować na Androida (Chrome)?</h3>
                  <ol className="list-decimal list-inside space-y-2 text-slate-700">
                    <li>Otwórz <strong>propertycheck.pro</strong> w przeglądarce Chrome</li>
                    <li>Naciśnij ikonę <strong>"Menu"</strong> (trzy kropki w prawym górnym rogu)</li>
                    <li>Wybierz <strong>"Dodaj do ekranu głównego"</strong> lub <strong>"Zainstaluj aplikację"</strong></li>
                    <li>Potwierdź instalację</li>
                    <li>Aplikacja zostanie dodana do ekranu głównego i listy aplikacji</li>
                  </ol>
                </div>

                <div className="border-t pt-6">
                  <h3 className="text-xl font-semibold text-slate-800 mb-3">Jak odinstalować?</h3>
                  <div className="space-y-3">
                    <div>
                      <h4 className="font-semibold text-slate-800">iPhone:</h4>
                      <p className="text-sm text-slate-600">
                        Przytrzymaj ikonę PropertyCheck.PRO na ekranie głównym → wybierz "Usuń aplikację"
                      </p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-800">Android:</h4>
                      <p className="text-sm text-slate-600">
                        Przytrzymaj ikonę → przeciągnij do kosza lub wybierz "Odinstaluj" z menu
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Troubleshooting Section */}
          <section id="troubleshooting" className="scroll-mt-20">
            <div className="border-t border-slate-200 pt-8 pb-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-lg bg-red-500 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-slate-800">Rozwiązywanie problemów</h2>
              </div>

              <div className="space-y-3">
                {troubleshootingFAQ.map((faq) => (
                  <div key={faq.id} className="border border-slate-200 rounded-xl overflow-hidden">
                    <button
                      onClick={() => toggleAccordion(faq.id)}
                      className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-slate-50 transition-colors"
                    >
                      <span className="font-semibold text-slate-800 pr-4">
                        {faq.question}
                      </span>
                      <svg
                        className={`w-5 h-5 text-slate-400 transition-transform flex-shrink-0 ${
                          openAccordion === faq.id ? 'transform rotate-180' : ''
                        }`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {openAccordion === faq.id && (
                      <div className="px-6 pb-4 text-slate-700 border-t border-slate-100">
                        <p className="pt-4">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
        {/* End of Sections */}

        </div>
        {/* End of White Card */}
      </div>
    </Layout>
  );
}
